import { type ReactNode, useRef, useState } from "react";
import { cn } from "@/lib/cn";

const TRIGGER = 72;
const MAX = 110;

/**
 * Pull down at the top to refresh (touch only), with the Reality Orb spinner. Works on the page itself
 * or on a scrolling pane. Native overscroll is contained so the browser's own refresh doesn't fire.
 */
export function PullToRefresh({ onRefresh, children, className }: { onRefresh: () => unknown; children: ReactNode; className?: string }) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const atTop = () => {
    const el = ref.current;
    const own = el && el.scrollHeight > el.clientHeight && getComputedStyle(el).overflowY !== "visible";
    return own ? el!.scrollTop <= 0 : window.scrollY <= 0;
  };
  return (
    <div
      ref={ref}
      className={cn("relative overflow-y-auto overscroll-y-contain", className)}
      onTouchStart={(e) => {
        if (busy || !atTop()) return;
        start.current = e.touches[0]?.clientY ?? null;
      }}
      onTouchMove={(e) => {
        if (start.current === null) return;
        const d = (e.touches[0]?.clientY ?? 0) - start.current;
        if (d <= 0) return setPull(0);
        setPull(Math.min(MAX, d * 0.5));
      }}
      onTouchEnd={async () => {
        const ready = pull >= TRIGGER * 0.75;
        start.current = null;
        if (!ready) return setPull(0);
        setBusy(true);
        setPull(56);
        try {
          await onRefresh();
        } finally {
          setBusy(false);
          setPull(0);
        }
      }}
    >
      <div
        aria-hidden={!busy}
        role={busy ? "status" : undefined}
        aria-label={busy ? "Refreshing" : undefined}
        className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center"
        style={{
          height: pull,
          opacity: Math.min(1, pull / TRIGGER),
          transition: start.current === null ? "height .2s, opacity .2s" : "none",
        }}
      >
        <span
          className={cn(
            "mt-3 size-9 rounded-full bg-gradient-to-br from-[#7C5CFF] to-[#22D3EE] shadow-[0_0_24px_rgba(124,92,255,0.65)]",
            busy && "animate-spin",
          )}
          style={{
            transform: busy ? undefined : `scale(${0.5 + Math.min(1, pull / TRIGGER) * 0.5}) rotate(${pull * 4}deg)`,
            backgroundImage: "conic-gradient(from 0deg, #7C5CFF, #22D3EE, #7C5CFF)",
            WebkitMask: "radial-gradient(circle, transparent 42%, #000 44%)",
            mask: "radial-gradient(circle, transparent 42%, #000 44%)",
          }}
        />
      </div>
      <div style={{ transform: pull ? `translateY(${pull}px)` : undefined, transition: start.current === null ? "transform .2s" : "none" }}>
        {children}
      </div>
    </div>
  );
}
